import React, { useState, useEffect } from "react";
import axios from "axios";
import "./SellerReviews.css";

function SellerReviews({ dealerId, dealerName }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const user = JSON.parse(
    localStorage.getItem("xompsmpsnd101010299msiknsloa11292nsinsi") || "null"
  );

  // Generate a simple browser fingerprint
  const getBrowserFingerprint = () => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillText("Browser fingerprint", 2, 2);

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      window.screen.width + "x" + window.screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
    ].join("|");

    // Create a simple hash
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  };

  useEffect(() => {
    fetchReviews();
  }, [dealerId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(
        `https://dashboard.maineautoparts.com/api/dealer/${dealerId}/reviews`
      );
      setReviews(response.data.reviews.data || []);
      setAverageRating(response.data.dealer.average_rating || 0);
      setTotalReviews(response.data.dealer.total_reviews || 0);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setError("");
    setSuccess("");
    setRating(0);
    setReviewText("");
    setReviewerName("");
    setReviewerEmail("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError("");
    setSuccess("");
    setRating(0);
    setReviewText("");
    setReviewerName("");
    setReviewerEmail("");
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (rating === 0) {
      setError("Please select a rating");
      return;
    }

    // For anonymous users, require name
    if (!user && !reviewerName.trim()) {
      setError("Please enter your name");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        dealer_id: dealerId,
        rating: rating,
        review: reviewText,
      };

      let browserFingerprint = null;

      // Add anonymous fields if not logged in
      if (!user) {
        payload.reviewer_name = reviewerName.trim();
        payload.reviewer_email = reviewerEmail.trim() || null;
        browserFingerprint = getBrowserFingerprint();
        payload.browser_fingerprint = browserFingerprint;

        // Store fingerprint in localStorage to prevent duplicate reviews
        const reviewKey = `review_${dealerId}_${browserFingerprint}`;
        const existingReview = localStorage.getItem(reviewKey);
        if (existingReview) {
          setError(
            "You have already submitted a review from this device. Please use a different device or login to update your review."
          );
          setSubmitting(false);
          return;
        }
      }

      const headers = {};
      if (user && user.accessToken) {
        headers.Authorization = `Bearer ${user.accessToken}`;
      }

      const response = await axios.post(
        "https://dashboard.maineautoparts.com/api/seller-review",
        payload,
        { headers }
      );

      setSuccess(response.data.message);

      // Store fingerprint in localStorage to prevent duplicate reviews
      if (!user && browserFingerprint) {
        const reviewKey = `review_${dealerId}_${browserFingerprint}`;
        localStorage.setItem(reviewKey, "true");
      }

      handleCloseModal();
      fetchReviews();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      setError(
        error.response?.data?.message ||
        "Error submitting review. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (ratingValue, interactive = false, size = "medium") => {
    return (
      <div className={`star-rating ${size}`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= ratingValue ? "filled" : ""} ${interactive ? "interactive" : ""
              }`}
            onClick={interactive ? () => setRating(star) : null}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const getInitials = (review) => {
    if (review.user) {
      const first = review.user.first_name?.[0] || "";
      const last = review.user.last_name?.[0] || "";
      return (first + last).toUpperCase();
    }
    const name = review.reviewer_name || "";
    if (!name) return "A";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0]?.toUpperCase() || "A";
  };

  const getReviewerName = (review) => {
    if (review.user) {
      const firstName = review.user.first_name || "";
      const lastName = review.user.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || "User";
    }
    return review.reviewer_name || "Anonymous";
  };

  // Generate a consistent color based on reviewer name/ID
  const getAvatarColor = (review) => {
    const name = getReviewerName(review);
    const id = review.user?.id || review.id || name;

    // Generate a hash from the name/id
    let hash = 0;
    const str = String(id) + name;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Generate a color from the hash
    const hue = Math.abs(hash % 360);
    const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
    const lightness = 50 + (Math.abs(hash) % 15); // 50-65%

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  if (loading) {
    return <div className="reviews-loading">Loading reviews...</div>;
  }

  return (
    <div className="seller-reviews-container">
      <div className="reviews-header">
        <h3>Seller Reviews</h3>
        <div className="rating-summary">
          <div className="average-rating">
            <span className="rating-number">{averageRating.toFixed(1)}</span>
            {renderStars(Math.round(averageRating), false, "large")}
            <span className="total-reviews">({totalReviews} reviews)</span>
          </div>
        </div>
      </div>

      <button className="btn-add-review" onClick={handleOpenModal}>
        Write a Review
      </button>

      {success && <div className="alert alert-success">{success}</div>}

      {/* Review Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Write a Review</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form className="review-form" onSubmit={handleSubmitReview}>
              {error && <div className="alert alert-error">{error}</div>}

              {!user && (
                <>
                  <div className="form-group">
                    <label htmlFor="reviewer-name">Your Name *</label>
                    <input
                      id="reviewer-name"
                      type="text"
                      value={reviewerName}
                      onChange={(e) => setReviewerName(e.target.value)}
                      placeholder="Enter your name"
                      required
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="reviewer-email">
                      Your Email (optional)
                    </label>
                    <input
                      id="reviewer-email"
                      type="email"
                      value={reviewerEmail}
                      onChange={(e) => setReviewerEmail(e.target.value)}
                      placeholder="Enter your email"
                      maxLength={255}
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Rating *</label>
                {renderStars(rating, true)}
              </div>

              <div className="form-group">
                <label htmlFor="review-text">Your Review</label>
                <textarea
                  id="review-text"
                  rows="4"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder="Share your experience with this seller..."
                  maxLength={1000}
                />
                <span className="char-count">{reviewText.length}/1000</span>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit-review"
                  disabled={submitting || rating === 0}
                >
                  {submitting ? "Submitting..." : "Submit Review"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="reviews-list">
        <h4>All Reviews</h4>
        {reviews.length === 0 ? (
          <p className="no-reviews">No reviews yet. Be the first to review!</p>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="review-item">
              <div className="review-header">
                <div className="reviewer-info">
                  <div
                    className="reviewer-avatar-circle"
                    style={{ backgroundColor: getAvatarColor(review) }}
                  >
                    {getInitials(review)}
                  </div>
                  <div>
                    <div className="reviewer-name">
                      {getReviewerName(review)}
                    </div>
                    <div className="review-date">
                      {new Date(review.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="review-rating">
                  {renderStars(review.rating)}
                </div>
              </div>
              {review.review && (
                <div className="review-text">{review.review}</div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default SellerReviews;
